import React, { useEffect, useState } from "react";
import { alertError, alertSuccess } from "Utils/alerts";
import { isValidIPv4CIDR } from "Utils/ip";
import { t } from "i18next";
import useFormState from "Utils/useFormState";
import QboxmailApi from "@/qboxmailApi";
import Input from "Components/input/Input";
import CopyableItem from "Components/CopyableItem/CopyableItem";
import Repeater from "Components/repeater/Repeater";
import Button from "Components/button/Button";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { isEmail } from "validator";
import Toggle from "Components/toggle/Toggle";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import ResponsiveButtonGroup from "Components/responsiveButtonGroup/ResponsiveButtonGroup";
import RadioButton from "Components/radiobutton/RadioButton";
import cx from "classnames";
import s from "../../delivery.scss";

const api = new QboxmailApi({ useCookie: true });

const Details = ({ accountData, onDetailsUpdated, loggedUser }) => {
  const {
    setFormFieldValue,
    formData,
    formErrors,
    setFormLoading,
    unsetFormLoading,
    isFormLoading,
    hasFormChanged,
    setInitialValues,
    resetToInitialState,
    setFormErrors,
  } = useFormState({});

  useEffect(() => {
    setInitialValues({
      description: accountData.description,
      bounceNotification: accountData.bounce_notification,
      bounceNotificationToSender: accountData.bounce_notification_to_sender ? "toSender" : "custom",
      relayIps: accountData.relay_ips,
      ignore_suppression_list: accountData.ignore_suppression_list,
    });
  }, [accountData, setInitialValues]);

  if (formData.bounceNotificationToSender === "toSender" && formErrors.bounce_notification) {
    setFormErrors({ bounce_notification: null });
  }

  if (formData.bounceNotificationToSender === "toSender" && formData.bounceNotification) {
    setFormFieldValue({ bounceNotification: null });
  }

  const [generatedPassword, setGeneratedPassword] = useState("");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  const validateForm = () => {
    if (formData && formData.bounceNotification && !isEmail(formData.bounceNotification)) {
      setFormErrors({ bounce_notification: t("errorMsg.notValid.emailAddress") });
      return false;
    }

    if (formData && formData.bounceNotificationToSender === "custom" && !formData.bounceNotification) {
      setFormErrors({ bounce_notification: t("errorMsg.missing.field") });
      return false;
    }

    return true;
  };

  const submitAccountInfo = async () => {
    setFormLoading();

    if (!validateForm()) {
      unsetFormLoading();
      return;
    }

    try {
      await api.updateDeliveryAccountInfo({
        id: accountData.id,
        ...formData,
        bounceNotificationToSender: formData.bounceNotificationToSender === "toSender",
      });
      await onDetailsUpdated();
      alertSuccess(t("alert.deliveryAccount.updated"));
      setFormErrors({});
    } catch (error) {
      console.log(error);
    }
    unsetFormLoading();
  };

  const generateNewPassword = async () => {
    setFormLoading();

    try {
      const resp = await api.generateNewDeliveryAccountPassword(accountData.id);
      if (resp) {
        setGeneratedPassword(resp.resources[0].password);
      }
    } catch (error) {
      console.error(error);
      alertError(t("alert.wrong"));
    }
    setIsPasswordModalOpen(false);
    unsetFormLoading();
  };

  const canEditPassword = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("edit_delivery_account_password")
  );

  return (
    <div className={cx(s.dashboardContainer, s.noMarginTop)}>
      <div className={s.dashboardHeader}>
        <ResponsiveButtonGroup
          buttonElements={[
            hasFormChanged ? (
              <Button onClick={resetToInitialState} color="outline" key={0}>
                button.cancel
              </Button>
            ) : null,
            <Button loading={isFormLoading} onClick={submitAccountInfo} key={1}>
              button.save
            </Button>,
          ]}
          warningElement={hasFormChanged && <WarningBanner color="yellow-text" msg={t("infoMsg.unsaveChanged")} />}
          className="flex items-center justify-end expand gap20"
        />
      </div>
      <div className={s.dashboardContent}>
        <div className={s.dashboardItem1}>
          <div className="settingTitle">{t("delivery.accounts.detailsAndSecurity.personalDescription.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.detailsAndSecurity.personalDescription.desc")}</div>
        </div>
        <div className={s.dashboardItem2}>
          <Input
            name="description"
            label={t("input.label.description")}
            placeholder={t("input.label.description")}
            onChange={setFormFieldValue}
            value={formData.description}
          />
        </div>
      </div>
      <div className={s.dashboardContent}>
        <div className={s.dashboardItem1}>
          <div className="settingTitle">{t("delivery.accounts.detailsAndSecurity.bounceNotification.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.detailsAndSecurity.bounceNotification.desc")}</div>
        </div>
        <div className={s.dashboardItem2}>
          <RadioButton
            className="inputFull2"
            name="bounceNotificationToSender"
            //eslint-disable-next-line
            children={[
              {
                value: "toSender",
                label: t("delivery.accounts.detailsAndSecurity.bounceNotification.label.toSender"),
              },
              {
                value: "custom",
                label: t("delivery.accounts.detailsAndSecurity.bounceNotification.label.custom"),
              },
            ]}
            value={formData.bounceNotificationToSender}
            onChange={setFormFieldValue}
          />
          <Input
            className={cx("marginLeft28", s.inputContainerNoWidth)}
            name="bounceNotification"
            placeholder={t("input.placeholder.email")}
            type="email"
            onChange={setFormFieldValue}
            value={formData.bounceNotificationToSender === "toSender" ? null : formData.bounceNotification}
            errors={formErrors.bounce_notification}
            disabled={formData.bounceNotificationToSender === "toSender"}
          />
        </div>
      </div>

      <div className={s.dashboardContent}>
        <div className={s.dashboardItem1}>
          <div className="settingTitle">{t("delivery.accounts.detailsAndSecurity.ignoreSuppressionList.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.detailsAndSecurity.ignoreSuppressionList.desc")}</div>
        </div>
        <div className={s.dashboardItem2}>
          <Toggle
            className="inputFull2"
            label={t("input.label.ignoreSuppressionList")}
            name="ignore_suppression_list"
            checked={formData.ignore_suppression_list}
            errors={formErrors.ignore_suppression_list}
            onChange={setFormFieldValue}
            hint={t("delivery.accounts.detailsAndSecurity.ignoreSuppressionList.hint")}
          />
        </div>
      </div>

      <div className={s.dashboardContent}>
        <div className={s.dashboardItem1}>
          <div className="settingTitle">{t("delivery.accounts.detailsAndSecurity.credentials.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.detailsAndSecurity.credentials.desc")}</div>
        </div>
        <div className={s.dashboardItem2}>
          <div className="text-sm">{t("input.label.hostname")}</div>
          <div className="">
            <CopyableItem value={accountData.config_hostname} asInput />
          </div>
          <div className="mt-6">
            <div className="text-sm">{t("input.label.username")}</div>
            <div className="">
              <CopyableItem value={accountData.username} asInput />
            </div>
          </div>
          <div className="mt-6">
            <div className="text-sm">{t("input.label.password")}</div>
            <div
              className={cx(s.linkButton, {
                "opacity-50 cursor-not-allowed": !canEditPassword,
              })}
              onClick={() => (canEditPassword ? setIsPasswordModalOpen(true) : setIsForbiddenModalOpen(true))}
            >
              {t("input.label.generateNewPassword")}
            </div>
            <div className="">
              <CopyableItem value={generatedPassword} asInput disabled={!generatedPassword} />
            </div>
          </div>
        </div>
      </div>

      {loggedUser.type === "Admin" && (
        <div className={s.dashboardContent}>
          <div className={s.dashboardItem1}>
            <div className="settingTitle">{t("delivery.accounts.detailsAndSecurity.enabledIps.title")}</div>
            <div className="sectionDesc">{t("delivery.accounts.detailsAndSecurity.enabledIps.desc")}</div>
          </div>
          <div className={s.dashboardItem2}>
            <Repeater
              className="inputFull2"
              labelInput={t("input.label.add.ipAddress")}
              label={t("delivery.accounts.detailsAndSecurity.enabledIps.tableTitle")}
              values={formData.relayIps}
              onChange={setFormFieldValue}
              maxValues={5}
              name="relayIps"
              errors={formErrors.relay_ips}
              placeholder={t("input.placeholder.ip")}
              validateNewValue={(newVal) => {
                if (!isValidIPv4CIDR(newVal)) {
                  alert(t("errorMsg.notValid.ip"));
                  return false;
                }
                return true;
              }}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        opened={isPasswordModalOpen}
        title={t("delivery.accounts.detailsAndSecurity.newPassword.modal.title")}
        text={"delivery.accounts.detailsAndSecurity.newPassword.modal.desc"}
        textButton="button.generate.password"
        isWarning
        translate
        handleClose={() => setIsPasswordModalOpen(false)}
        action={generateNewPassword}
        isLoading={isFormLoading}
      />

      <ConfirmModal
        title={t("teamMembers.forbiddenAction")}
        text={t("teamMembers.forbiddenActionSubtitle")}
        textButton="button.ok"
        hideCancelButton={true}
        opened={isForbiddenModalOpen}
        action={() => setIsForbiddenModalOpen(false)}
        handleClose={() => setIsForbiddenModalOpen(false)}
        buttonColor="accent"
      />
    </div>
  );
};

export default Details;
